import type { PbpCoverage, PbpRollup } from '@/types/pbp';

export function organizationLabel(row: { organization_id: number; organization_short_name?: string | null; organization_name?: string | null }) {
  return row.organization_short_name ?? row.organization_name ?? `Organization ${row.organization_id}`;
}

/** Sum complete server aggregate rows within ONE org; never average percentages. */
export function organizationRollups(rows: PbpRollup[]): PbpRollup[] {
  const groups = new Map<number, PbpRollup>();
  const counts = ['universe', 'pbp_present', 'scorer_absent', 'pending_collection', 'excluded_non_entitled', 'superseded_count', ...PENDING_REASONS.map(([key]) => key)] as const;
  for (const row of rows) {
    const current = groups.get(row.organization_id);
    if (!current) groups.set(row.organization_id, { ...row });
    else for (const key of counts) current[key] = Number(current[key]) + Number(row[key]);
  }
  return [...groups.values()].map((row) => ({ ...row, coverage_pct: Number(row.universe) ? Number(row.pbp_present) / Number(row.universe) * 100 : null }))
    .sort((a, b) => {
      const rank = (row: PbpRollup) => organizationLabel(row) === 'NJCAA' ? -1 : organizationLabel(row) === 'Default organization' ? 1 : 0;
      return rank(a) - rank(b) || organizationLabel(a).localeCompare(organizationLabel(b));
    });
}

export const PENDING_REASONS = [
  ['pending_held_unparsed', 'Processing queued'],
  ['pending_parse_error', 'Parse error'],
  ['pending_parse_anomaly', 'Parse anomaly'],
  ['pending_awaiting_final_doc', 'Awaiting final document'],
  ['pending_not_yet_collected', 'Not yet collected'],
  ['pending_read_error', 'Source read failed'],
  ['pending_no_start_time', 'Start time unavailable'],
] as const;

const processingLabels: Record<string, string> = {
  'held-unparsed': 'Processing queued',
  'parse-error': 'Parse error',
  'parse-anomaly-zero-plays': 'Parse anomaly',
  'awaiting-final-doc': 'Awaiting final document',
  'not-yet-collected': 'Not yet collected',
  'read-error': 'Source read failed',
  'no-start-time': 'Start time unavailable',
};

export function evidenceGrade(row: PbpCoverage): string {
  const routes = row.absence_evidence?.routes_checked;
  if (!Array.isArray(routes)) return 'Evidence unavailable';
  if (routes.includes('A') && routes.includes('B')) return 'A+B checked';
  if (routes.includes('A')) return 'A checked';
  return 'Evidence unavailable';
}

/** Presentation only: designation and processing precedence belong to the view. */
export function coverageState(row: PbpCoverage) {
  if (!row.in_universe) return { label: `Outside completed-game coverage · ${row.event_status}`, reason: 'Excluded from completed-game totals.', tone: 'outline' as const };
  switch (row.designation) {
    case 'pbp-present': return { label: 'Has plays', reason: row.superseded ? 'Prior absence superseded; original evidence retained.' : 'Stored plays available.', tone: 'success' as const };
    case 'pbp-scorer-absent': return { label: `Scorer absent · ${evidenceGrade(row)}`, reason: evidenceGrade(row) === 'A checked' ? 'No plays marker in the checked /stats document.' : 'See checked routes and their evidence.', tone: 'default' as const };
    case 'pbp-non-entitled': return { label: 'Excluded · non-entitled', reason: 'Canonical route-A denial; excluded from coverage denominator.', tone: 'outline' as const };
    case 'pbp-pending-collection': return { label: 'Pending', reason: processingLabels[row.processing_state] ?? `Processing state: ${row.processing_state}`, tone: ['read-error', 'parse-error'].includes(row.processing_state) ? 'danger' as const : 'warning' as const };
    default: return { label: 'Unknown coverage state', reason: row.designation, tone: 'warning' as const };
  }
}

export function revisionNotice(row: PbpCoverage): string | null {
  if (row.plays_manifest_id == null || row.classified_manifest_id == null || row.plays_manifest_id === row.classified_manifest_id) return null;
  return `Showing stored plays from manifest ${row.plays_manifest_id}. Classified manifest ${row.classified_manifest_id}: ${row.classified_outcome ?? 'outcome unavailable'}.`;
}

export function pageNumber(value: string | null): number {
  const page = Number(value ?? 0);
  return Number.isSafeInteger(page) && page >= 0 && page <= 100000 ? page : 0;
}

export function utc(value: string | null | undefined): string {
  return value ? new Date(value).toISOString().replace('T', ' ').replace(/\.\d{3}Z$/, ' UTC') : 'Unavailable';
}
